import { Grid, Typography } from '@material-ui/core';
import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';

import BoldInput from './inputs/BoldInput';
import { getPlaidUser, fetchPlaidUser } from '../modules/factoringClient';
import moment from 'moment/moment';
import PlaidJsonTable from './PlaidJsonTable';
import { get } from 'lodash';

const PlaidInfo = ({ userId }) => {
  const dispatch = useDispatch();
  useEffect(() => {
    if (userId) {
      dispatch(fetchPlaidUser(userId));
    }
  }, [userId]);
  const {
    accounts,
    name,
    address,
    city,
    state,
    zip,
    phone,
    email,
    bankAccount,
    routingNumber,
    bankName,
    created,
    accountEntryMethod
  } = useSelector(getPlaidUser);

  // Find the default account and determine if there's a newer account
  const accountsWithNewer = useMemo(() => {
    if (!accounts || accounts.length === 0) {
      return [];
    }

    // Find the default account
    const defaultAccount = accounts.find(account => account.default);

    // If no default account, return the original accounts
    if (!defaultAccount || !defaultAccount.created) {
      return accounts;
    }

    const defaultCreatedDate = new Date(defaultAccount.created);

    // Add newer flag to accounts with creation dates newer than default account
    return accounts.map(account => {
      if (!account.created || account.default) {
        return account;
      }

      const accountCreatedDate = new Date(account.created);
      return {
        ...account,
        newer: accountCreatedDate > defaultCreatedDate
      };
    });
  }, [accounts]);

  const bankInfoDisplay = useMemo(() => {
    // Initialize with original values
    let displayBankAccount = bankAccount || '';
    let displayRoutingNumber = routingNumber || '';
    let displayBankName = bankName || '';

    // If any value is empty and we have accounts, try to get values from accounts
    if (
      (!displayBankAccount || !displayRoutingNumber || !displayBankName) &&
      accounts &&
      accounts.length > 0
    ) {
      // First try to get from default account
      const defaultAccount = accounts.find(account => account.default);

      if (defaultAccount) {
        if (!displayBankAccount)
          displayBankAccount = get(
            defaultAccount,
            'account_number_redacted',
            ''
          );
        if (!displayRoutingNumber)
          displayRoutingNumber = get(defaultAccount, 'routing_number', '');
        if (!displayBankName)
          displayBankName = get(defaultAccount, 'bank_name', '');
      }
      // If still empty or no default account, get from newest account with preference for checking accounts
      else {
        // Sort accounts by creation date (newest first)
        const sortedAccounts = [...accounts].sort((a, b) => {
          if (!a.created) return 1;
          if (!b.created) return -1;
          return new Date(b.created) - new Date(a.created);
        });

        // First try to find a checking account among the newest accounts
        const checkingAccount = sortedAccounts.find(
          account => get(account, 'method', '').toLowerCase() === 'ach'
        );

        // Use the checking account if found (for ACH), otherwise use the newest account
        const accountToUse = checkingAccount || sortedAccounts[0];

        if (accountToUse) {
          if (!displayBankAccount)
            displayBankAccount = get(
              accountToUse,
              'account_number_redacted',
              ''
            );
          if (!displayRoutingNumber)
            displayRoutingNumber = get(accountToUse, 'routing_number', '');
          if (!displayBankName)
            displayBankName = get(accountToUse, 'bank_name', '');
        }
      }
    }

    return {
      displayBankAccount,
      displayRoutingNumber,
      displayBankName,
      accounts
    };
  }, [bankAccount, routingNumber, bankName, accounts]);

  const hasDefaultAccount =
    accounts && accounts.some(account => account.default);
  const hasNewerAccount =
    accountsWithNewer && accountsWithNewer.some(account => account.newer);

  // Determine if we should display the Plaid form
  const shouldShowPlaidForm = accountEntryMethod !== 'manually';

  return (
    <>
      {shouldShowPlaidForm ? (
        <Grid container direction="row" spacing={32}>
          <Grid item xs={4}>
            {accounts.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                {hasDefaultAccount && (
                  <CheckCircleIcon
                    style={{ color: 'green', marginRight: '5px' }}
                  />
                )}
                {hasDefaultAccount && hasNewerAccount && (
                  <WarningIcon
                    style={{ color: 'orange', marginRight: '5px' }}
                  />
                )}
                <Typography
                  variant="body2"
                  style={{
                    color:
                      hasDefaultAccount && hasNewerAccount ? 'orange' : 'green',
                    fontWeight: 'bold'
                  }}
                >
                  {hasDefaultAccount && !hasNewerAccount
                    ? 'Most Recent Account Set As Default'
                    : hasDefaultAccount && hasNewerAccount
                    ? 'Default Account Linked, but a newer account is available'
                    : 'No Default Account Linked'}
                </Typography>
              </div>
            )}
            <BoldInput
              disabled
              fullWidth
              label="Plaid Sync Date"
              value={created ? moment(created).format('l LTS') : ''}
            />
            <BoldInput disabled fullWidth label="Plaid Name" value={name} />
            <BoldInput
              disabled
              fullWidth
              label="Plaid Identity Address"
              value={address}
            />
            <BoldInput disabled fullWidth label="Plaid Identity City" value={city} />
          </Grid>
          <Grid item xs={4}>
            <BoldInput disabled fullWidth label="Plaid Identity State" value={state} />
            <BoldInput disabled fullWidth label="Plaid Identity Zip" value={zip} />
            <BoldInput disabled fullWidth label="Plaid Phone" value={phone} />
            <BoldInput disabled fullWidth label="Plaid Email" value={email} />
          </Grid>
          <Grid item xs={4}>
            <BoldInput
              disabled
              fullWidth
              label="Plaid Bank Account"
              value={bankInfoDisplay.displayBankAccount}
            />
            <BoldInput
              disabled
              fullWidth
              label="Plaid Routing Number"
              value={bankInfoDisplay.displayRoutingNumber}
            />
            <BoldInput
              disabled
              fullWidth
              label="Plaid Bank Name"
              value={bankInfoDisplay.displayBankName}
            />
          </Grid>
        </Grid>
      ) : (
        <Grid container direction="row" spacing={32}>
          <Grid item xs={12}>
            <Typography variant="body1" style={{ marginBottom: '20px' }}>
              No plaid account selected as default
            </Typography>
          </Grid>
        </Grid>
      )}

      {accounts.length > 0 && (
        <Grid item>
          <PlaidJsonTable
            data={accountsWithNewer}
            title={`Load more Plaid objects (count: ${accountsWithNewer.length})`}
            omitFields={[
              'id',
              'deleted_by',
              'modified',
              'deleted_reason',
              'newer',
              'factoring_relation_id',
              'card_number',
              'expiration_date',
              'name'
            ]}
            autoExpand
          />
        </Grid>
      )}
    </>
  );
};

export default PlaidInfo;
